<template>
  <div v-if="displayRelatedQueries" :class="positionClass('related-queries', '__')">
    <h3 :class="positionClass('related-queries__header','-')" data-test-id="related-queries-header">
      {{ $t('web.relatedQueries.title') }} {{ query }}
    </h3>
    <div :class="positionClass('related-queries__links', '-')">
      <ELinkBubble
        v-for="(relatedQuery, index) in relatedLinks"
        :key="relatedQuery.query"
        :href="getUrl(relatedQuery.query)"
        :color="relatedQuery.linkColor"
        :class="positionClass('related-queries__link', '-')"
        size="l"
        :title="relatedQuery.query"
        :data-test-id="getQueryId(relatedQuery.query)"
        at-breakpoint="desktop"
        @click="onLinkClick(relatedQuery.query, index, relatedQuery.isGreen)"
      >
        <div class="related-queries__text-container">
          <span
            v-safe-html="queryHtml(relatedQuery.query)"
            class="related-queries__text"
            data-test-id="related-queries-text"
          />
        </div>
        <EIcon
          v-if="relatedQuery.isGreen"
          data-test-id="related-queries-green-leaf-icon"
          aria-hidden="true"
          icon="green-leaf"
          size="m"
          class="related-queries__icon"
        />
        <EIcon
          aria-hidden="true"
          data-test-id="related-queries-search-icon"
          icon="search"
          size="s"
          class="related-queries__icon"
        />
      </ELinkBubble>
    </div>
    <EGreenQueriesExplainer v-if="hasGreenSuggestion" class="related-queries__green-explainer" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import kebabCase from 'lodash/kebabCase';

import { getUrlQuery, baseUrlParams } from '~~/common/js/universal/search';
import ELinkBubble from '~~/common/vue/components/link-bubble';
import EIcon from '~~/common/vue/components/icon';
import EGreenQueriesExplainer from './green-queries-explainer';
import {
  GREEN_FEATURES,
  GREEN_RELATED_SEARCHES,
} from '~~/search/common/js/client/feature-flags';
import { trackClick, TYPE_RELATED_SEARCH } from '~~/search/common/js/client/analytics/results';
import relatedQueriesImpression from '~~/search/common/js/client/analytics/related-queries';
import { wrapNotMatching } from '~/utils';

const renderPositions = ['bottom', 'sidebar'];

export default {
  components: {
    ELinkBubble,
    EIcon,
    EGreenQueriesExplainer,
  },
  props: {
    query: {
      type: String,
      required: true,
    },
    position: {
      type: String,
      default: 'bottom',
      validator: (value) => renderPositions.includes(value),
    },
  },
  computed: {
    ...mapGetters('goodall', ['getExperiment']),
    ...mapState(['relatedQueries', 'relatedSearches']),
    isGreenExperimentEnabled() {
      return this.getExperiment(GREEN_FEATURES.flag)?.includes(GREEN_FEATURES.enabled) &&
             this.getExperiment(GREEN_RELATED_SEARCHES.flag)
               ?.includes(GREEN_RELATED_SEARCHES.enabled);
    },
    hasRelatedQueries() {
      return this.relatedQueries && this.relatedQueries?.length > 0;
    },
    hasRelatedSearches() {
      return this.relatedSearches && this.relatedSearches?.length > 0;
    },
    queryHtml() {
      return wrapNotMatching(this.query, 'strong');
    },
    relatedLinks() {
      if (this.hasRelatedSearches && this.isGreenExperimentEnabled) {
        return this.relatedSearches?.map((search) => ({ query: search.query, linkColor: 'result', isGreen: search.isGreen }));
      }
      return this.relatedQueries?.map((query) => ({ query, linkColor: 'result', isGreen: false }));
    },
    displayRelatedQueries() {
      if (!this.hasRelatedQueries && !this.hasRelatedSearches) {
        return false;
      }
      if (!this.hasRelatedSearches && this.position === renderPositions[1]) {
        return false;
      }
      if (this.position === renderPositions[1] && !this.isGreenExperimentEnabled) {
        return false;
      }
      if (this.position === renderPositions[1] &&
        !this.relatedLinks?.some((item) => item.isGreen === true)) {
        return false;
      }
      return true;
    },
    hasGreenSuggestion() {
      const present = this.relatedLinks?.some((query) => (query.isGreen === true));
      return present;
    },
    impressionsTrackingOptions() {
      const impressions = this.relatedLinks?.map((link, index) => ({
        title: link.query,
        url: this.getUrl(link.query),
        rank: index + 1,
        result_type: 'related-search',
        result_label: link.isGreen ? 'green' : null }),
      );
      return {
        query: this.query,
        impressions,
        page_position: this.position,
      };
    },
  },
  mounted() {
    if (this.displayRelatedQueries) {
      this.$track(relatedQueriesImpression(this.impressionsTrackingOptions));
    }
  },
  methods: {
    clickTrackingOptions(title, index, isGreen = false) {
      return {
        position: this.position,
        url: this.getUrl(title),
        rank: index + 1,
        title,
        type: TYPE_RELATED_SEARCH,
        label: isGreen ? 'green' : '',
      };
    },
    getUrl(q) {
      const query = {
        ...getUrlQuery(
          this.$route.query,
          this.$store.state.user,
          [...baseUrlParams, 'canary', 'mocked', 'forceProvider'],
        ),
        q,
      };
      return this.$router.resolve({ query }).href;
    },
    getQueryId(query) {
      return `related-queries-link-${kebabCase(query)}`;
    },
    onLinkClick(query, index, isGreen) {
      trackClick(this.clickTrackingOptions(query, index, isGreen));
    },
    positionClass(klass, separator) {
      return `${klass}${separator}${this.position}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.related-queries__bottom {
  background: var(--color-background-primary);

  @include desktop {
    padding: 0 $space-m;
  }
}

.related-queries__sidebar {
  @include elevation(0);

  border: solid 1px var(--color-decorative-border-1);
  border-radius: $border-radius-l;
  padding: 0 $space-m $space-m;

  .link-bubble {
    &:hover {
      background-color: unset;
    }
  }
}

.related-queries__text-container {
  min-width: 0;
  width: 100%;
}

.related-queries__text-icon {
  margin-left: $space-3s;
}

.related-queries__header-bottom {
  margin: 0;
  font-size: $font-l;
  font-weight: $font-weight-m;
  color: var(--color-text-primary);
  padding: $space-m $space-m $space-1s;

  @include desktop {
    padding: $space-m 0;
  }
}

.related-queries__header-sidebar {
  margin: 0;
  font-size: $font-m;
  font-weight: $font-weight-m;
  color: var(--color-text-secondary);
  padding: $space-m $space-m $space-1s;

  @include divider('bottom');

  @include desktop {
    padding: $space-m 0;
  }
}

.related-queries__links-bottom {
  padding: 0 $space-m $space-1s;

  @include desktop {
    display: grid;
    grid-template-columns: repeat(2, 50%);
    row-gap: $space-s;
    column-gap: $space-m;
    padding: 0 $space-s $space-l;
    padding: 0;
  }
}

.related-queries__links-sidebar {
  padding: 0;
}

.related-queries__text {
  @include line-clamp(2);

  min-width: 0;
  overflow-wrap: break-word;
}

.related-queries__icon {
  flex-shrink: 0;
  color: var(--color-decorative-icon);
  transition: color $timing-2s $easing;
  margin-left: $space-1s;
}

.related-queries__link-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;

  @include desktop-down {
    padding: $space-m 0;

    &:not(:last-child) {
      @include divider('bottom');
    }
  }
}

.related-queries__link-sidebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  border: 0;
  border-radius: 0;

  &:not(:first-child) {
    @include divider('top');
  }

  @include desktop-down {
    padding: 0;

    &:not(:last-child) {
      @include divider('bottom');
    }
  }
}

.related-queries__green-explainer {
  margin-top: $space-m;
}
</style>
